import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuthState } from 'react-firebase-hooks/auth';
import { signInWithCredential, signInWithPopup, signInWithRedirect, getRedirectResult, GoogleAuthProvider } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../../firebase';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;
const ONE_TAP_DELAY_MS = 1200;
const POST_AUTH_CRT_SESSION_KEY = 'ratafit_expect_post_auth_crt';
const OAUTH_REDIRECT_PENDING_KEY = 'ratafit_oauth_redirect_pending';

/** Una sola llamada: StrictMode en desarrollo invocaría getRedirectResult dos veces y perdería el resultado. */
const redirectResultPromise = typeof window !== 'undefined'
  ? getRedirectResult(auth)
  : Promise.resolve(null);

const POPUP_FALLBACK_TO_REDIRECT_CODES = new Set([
  'auth/popup-blocked',
  'auth/operation-not-supported-in-this-environment',
  'auth/web-storage-unsupported'
]);

function humanizeAuthError(err) {
  const code = err?.code || '';
  if (code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request') {
    return 'Inicio de sesión cancelado.';
  }
  if (code === 'auth/network-request-failed') {
    return 'Sin conexión. Revisa internet e intenta de nuevo.';
  }
  if (code === 'auth/unauthorized-domain') {
    return 'Este dominio no está autorizado para iniciar sesión.';
  }
  if (code === 'auth/invalid-credential') {
    return 'No se pudo validar la sesión de Google. Intenta de nuevo.';
  }
  return 'No se pudo completar el inicio de sesión. Intenta de nuevo.';
}

// Contexto para autenticación
const AuthContext = createContext();

// Hook personalizado para usar el contexto de autenticación
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe usarse dentro de AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, loading, error] = useAuthState(auth);
  const [userProfileData, setUserProfileData] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isUserBlocked, setIsUserBlocked] = useState(false);
  const [blockInfo, setBlockInfo] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [loginInProgress, setLoginInProgress] = useState(() => {
    try {
      return sessionStorage.getItem(POST_AUTH_CRT_SESSION_KEY) === '1'
        || sessionStorage.getItem(OAUTH_REDIRECT_PENDING_KEY) === '1';
    } catch (_) {
      return false;
    }
  });
  const [loginError, setLoginError] = useState(null);
  const [redirectSettled, setRedirectSettled] = useState(false);
  const oneTapShownRef = useRef(false);

  // En app nativa (Capacitor): WebView no maneja bien popup/redirect; usamos plugin nativo.
  const isCapacitorNative = typeof window !== 'undefined' && window.Capacitor?.isNativePlatform?.();

  // En PWA o móvil web: One Tap usa FedCM y deja el botón inutil (solo una petición a la vez). Popup, no redirect.
  const isMobileOrPWA = typeof window !== 'undefined' && (
    window.matchMedia('(pointer: coarse)').matches ||
    window.matchMedia('(display-mode: standalone)').matches ||
    /iPhone|iPad|iPod|Android/i.test(navigator.userAgent || '')
  );

  // Login nativo con plugin Capacitor (solo en app instalada). Devuelve credential para Firebase.
  const signInWithNativeGoogle = useCallback(async () => {
    let GoogleAuth;
    try {
      const mod = await import('@codetrix-studio/capacitor-google-auth');
      GoogleAuth = mod.GoogleAuth;
    } catch (e) {
      console.error('Plugin Google Auth no disponible:', e);
      throw new Error('Login con Google no disponible. Prueba en un dispositivo físico con Google Play.');
    }
    if (!GoogleAuth?.signIn) {
      throw new Error('Login con Google no disponible en esta versión.');
    }
    try {
      // Inicializar por si el plugin lo requiere (p. ej. en algunos entornos)
      if (typeof GoogleAuth.initialize === 'function') {
        GoogleAuth.initialize({
          scopes: ['profile', 'email'],
          serverClientId: '257255833486-jusojlsi6244n839t59csais2q8hfah3.apps.googleusercontent.com'
        });
      }
      const googleUser = await GoogleAuth.signIn();
      if (!googleUser?.authentication?.idToken) {
        throw new Error('Inicio de sesión cancelado o sin token');
      }
      const credential = GoogleAuthProvider.credential(googleUser.authentication.idToken);
      await signInWithCredential(auth, credential);
    } catch (err) {
      const msg = err?.message || String(err);
      if (msg.includes('cancelado') || msg.includes('cancelled') || msg.includes('12501')) {
        throw new Error('Inicio de sesión cancelado.');
      }
      console.error('Error login nativo Google:', err);
      throw err;
    }
  }, []);

  // Iniciar sesión en Firebase con el ID token de Google One Tap (solo web)
  const handleCredentialResponse = useCallback(async (response) => {
    if (!response?.credential) return;
    try {
      const credential = GoogleAuthProvider.credential(response.credential);
      await signInWithCredential(auth, credential);
    } catch (err) {
      console.error('Error al iniciar sesión con One Tap:', err);
      setLoginInProgress(false);
    }
  }, []);

  // Web: popup primero. En iOS/Safari, signInWithRedirect suele “rebotar” (ITP pierde sessionStorage
  // al volver de Google) y getRedirectResult queda vacío, sin error. Redirect solo si el popup no puede abrirse.
  const signInWithPopupFallback = useCallback(async () => {
    const provider = new GoogleAuthProvider();
    try {
      await signInWithPopup(auth, provider);
    } catch (err) {
      const code = err?.code || '';
      if (isCapacitorNative) {
        console.warn('Login en app: usar flujo nativo.', err);
        throw err;
      }
      if (POPUP_FALLBACK_TO_REDIRECT_CODES.has(code)) {
        try {
          sessionStorage.setItem(OAUTH_REDIRECT_PENDING_KEY, '1');
        } catch (_) { /* ignore */ }
        await signInWithRedirect(auth, provider);
        return;
      }
      throw err;
    }
  }, [isCapacitorNative]);

  // Abrir login en navegador (fallback cuando el plugin nativo falla, p. ej. en emulador)
  const AUTH_APP_URL = 'https://www.ratafit.com/auth-app';
  const openBrowserLogin = useCallback(async () => {
    try {
      const { Browser } = await import('@capacitor/browser');
      await Browser.open({ url: AUTH_APP_URL });
    } catch (_) {
      window.open(AUTH_APP_URL, '_blank');
    }
  }, []);

  // Iniciar sesión con Google: en app nativa → plugin; en PWA/móvil → redirect; en web escritorio → One Tap o popup
  const signInWithGoogle = useCallback(async () => {
    if (loginInProgress) return;
    setLoginInProgress(true);
    setLoginError(null);
    try {
      if (isCapacitorNative) {
        try {
          await signInWithNativeGoogle();
        } catch (err) {
          console.error('Error login nativo Google:', err);
          await openBrowserLogin();
          setLoginInProgress(false);
          return;
        }
        setLoginInProgress(false);
        return;
      }

      // PWA o móvil: no usar One Tap (FedCM deja el botón inutil). Popup, no redirect (Safari iOS).
      if (isMobileOrPWA) {
        await signInWithPopupFallback();
        return;
      }

      // Escritorio: si ya mostramos One Tap una vez (o canceló), no repetir (solo 1 credentials.get a la vez)
      if (oneTapShownRef.current) {
        await signInWithPopupFallback();
        return;
      }

      const gsi = typeof window !== 'undefined' && window.google?.accounts?.id;
      if (GOOGLE_CLIENT_ID && gsi) {
        oneTapShownRef.current = true;
        try {
          window.google.accounts.id.initialize({
            client_id: GOOGLE_CLIENT_ID,
            callback: (r) => {
              void handleCredentialResponse(r);
            },
            cancel_on_tap_outside: true,
            cancel_callback: () => {
              void signInWithPopupFallback().catch(() => setLoginInProgress(false));
            }
          });
          window.google.accounts.id.prompt();
          return;
        } catch (e) {
          oneTapShownRef.current = false;
          await signInWithPopupFallback();
          return;
        }
      }

      await signInWithPopupFallback();
    } catch (err) {
      setLoginInProgress(false);
      setLoginError(humanizeAuthError(err));
      throw err;
    }
    // Popup / One Tap en escritorio: no poner loginInProgress en false aquí — `user` puede llegar un tick después
    // del cierre del popup y WelcomePage limpiaba el CRT con !user && !loginInProgress. Se resetea en useEffect.
  }, [loginInProgress, isCapacitorNative, isMobileOrPWA, signInWithNativeGoogle, openBrowserLogin, handleCredentialResponse, signInWithPopupFallback]);

  /**
   * Bienvenida: en escritorio solo popup (sin One Tap), para que el flujo CRT coincida con el cierre del popup.
   * En móvil/PWA se usa popup (redirect solo si el popup está bloqueado). Misma política de loginInProgress que signInWithGooglePopup en escritorio.
   */
  const signInWithGoogleForWelcome = useCallback(async () => {
    if (loginInProgress) return;
    setLoginInProgress(true);
    setLoginError(null);
    try {
      if (isCapacitorNative) {
        try {
          await signInWithNativeGoogle();
        } catch (err) {
          console.error('Error login nativo Google:', err);
          await openBrowserLogin();
          setLoginInProgress(false);
          return;
        }
        setLoginInProgress(false);
        return;
      }

      if (isMobileOrPWA) {
        await signInWithPopupFallback();
        return;
      }

      await signInWithPopupFallback();
    } catch (err) {
      setLoginInProgress(false);
      setLoginError(humanizeAuthError(err));
      throw err;
    }
  }, [loginInProgress, isCapacitorNative, isMobileOrPWA, signInWithNativeGoogle, openBrowserLogin, signInWithPopupFallback]);

  // Iniciar sesión siempre con popup (sin One Tap ni redirect inicial). Útil en perfiles públicos para no salir de la página.
  const signInWithGooglePopup = useCallback(async () => {
    if (loginInProgress) return;
    setLoginInProgress(true);
    setLoginError(null);
    try {
      if (isCapacitorNative) {
        try {
          await signInWithNativeGoogle();
        } catch (err) {
          console.error('Error login nativo Google:', err);
          await openBrowserLogin();
        }
        setLoginInProgress(false);
        return;
      }
      await signInWithPopupFallback();
    } catch (err) {
      setLoginInProgress(false);
      setLoginError(humanizeAuthError(err));
      return;
    }
    // Popup OK: no hacer setLoginInProgress(false) aquí — si no, React aún tiene user=null y WelcomePage borra el CRT pendiente.
    // Se limpia en el useEffect cuando exista `user`.
  }, [loginInProgress, isCapacitorNative, signInWithNativeGoogle, openBrowserLogin, signInWithPopupFallback]);

  // Login por redirección: solo en navegador. En app nativa (Capacitor) no usar: devuelve a localhost y falla.
  const signInWithGoogleRedirect = useCallback(() => {
    if (isCapacitorNative) {
      // En la app instalada usar popup/One Tap; redirect lleva a "localhost rechazó la conexión"
      signInWithPopupFallback().catch((e) => console.error('Error login en app:', e));
      return;
    }
    const provider = new GoogleAuthProvider();
    try {
      sessionStorage.setItem(OAUTH_REDIRECT_PENDING_KEY, '1');
    } catch (_) { /* ignore */ }
    signInWithRedirect(auth, provider);
  }, [isCapacitorNative, signInWithPopupFallback]);

  // One Tap automático al cargar cuando no hay usuario (solo web escritorio; en app nativa o PWA/móvil no)
  useEffect(() => {
    if (isCapacitorNative || isMobileOrPWA || user || loading || !GOOGLE_CLIENT_ID) return;

    const timer = setTimeout(() => {
      if (user) return;
      const gsi = window.google?.accounts?.id;
      if (!gsi || oneTapShownRef.current) return;

      oneTapShownRef.current = true;
      try {
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: handleCredentialResponse,
          cancel_on_tap_outside: true
        });
        window.google.accounts.id.prompt();
      } catch (_) {
        oneTapShownRef.current = false;
      }
    }, ONE_TAP_DELAY_MS);

    return () => clearTimeout(timer);
  }, [isCapacitorNative, isMobileOrPWA, user, loading, handleCredentialResponse]);

  // Completar login al volver de signInWithRedirect (fallback si el popup no pudo abrirse)
  useEffect(() => {
    let cancelled = false;
    redirectResultPromise
      .catch((err) => {
        if (cancelled) return;
        console.error('Error en redirect result:', err);
        setLoginError(humanizeAuthError(err));
        setLoginInProgress(false);
      })
      .finally(() => {
        if (!cancelled) setRedirectSettled(true);
      });
    return () => { cancelled = true; };
  }, []);

  // Tras redirect: si volvimos de Google sin sesión, mostrar error (antes era un rebote silencioso)
  useEffect(() => {
    if (!redirectSettled || loading) return;
    if (user) {
      try {
        sessionStorage.removeItem(OAUTH_REDIRECT_PENDING_KEY);
      } catch (_) { /* ignore */ }
      return;
    }
    try {
      const pendingRedirect = sessionStorage.getItem(OAUTH_REDIRECT_PENDING_KEY) === '1';
      const pendingCrt = sessionStorage.getItem(POST_AUTH_CRT_SESSION_KEY) === '1';
      if (pendingRedirect || pendingCrt) {
        sessionStorage.removeItem(OAUTH_REDIRECT_PENDING_KEY);
        sessionStorage.removeItem(POST_AUTH_CRT_SESSION_KEY);
        setLoginError((prev) => prev || 'No se pudo completar el inicio de sesión. En iPhone el retorno de Google a veces se pierde; prueba de nuevo.');
      }
    } catch (_) { /* ignore */ }
    setLoginInProgress(false);
  }, [redirectSettled, loading, user]);

  // En app: escuchar deep link ratafit://auth#token (login desde navegador)
  useEffect(() => {
    if (!isCapacitorNative || typeof window === 'undefined') return;
    let listener;
    const setup = async () => {
      try {
        const { App } = await import('@capacitor/app');
        listener = await App.addListener('appUrlOpen', ({ url }) => {
          if (!url || !url.includes('ratafit://auth')) return;
          try {
            const u = new URL(url);
            const token = u.searchParams.get('token') || (url.includes('#') ? decodeURIComponent(url.split('#')[1] || '').trim() : '');
            if (!token) return;
          const credential = GoogleAuthProvider.credential(token);
          signInWithCredential(auth, credential).catch((e) => console.error('Error signIn con token deep link:', e));
          } catch (_) {}
        });
      } catch (e) {
        console.warn('App listener no disponible:', e);
      }
    };
    setup();
    return () => { if (listener?.remove) listener.remove(); };
  }, [isCapacitorNative]);

  // Reset ref cuando el usuario cierra sesión para poder mostrar One Tap de nuevo
  useEffect(() => {
    if (!user) oneTapShownRef.current = false;
  }, [user]);

  // Tras popup/credencial: `user` puede llegar un tick después que termine la promesa; mantener loginInProgress hasta entonces
  useEffect(() => {
    if (user && loginInProgress) {
      setLoginInProgress(false);
    }
    if (user) setLoginError(null);
  }, [user, loginInProgress]);

  // Cargar datos del perfil del usuario
  useEffect(() => {
    const loadUserProfile = async () => {
      if (!user) {
        setUserProfileData(null);
        setIsAdmin(false);
        setIsUserBlocked(false);
        setBlockInfo(null);
        setIsInitialized(true);
        return;
      }

      try {
        const userRef = doc(db, 'users', user.uid);
        const userDoc = await getDoc(userRef);
        
        if (userDoc.exists()) {
          const data = userDoc.data();
          setUserProfileData(data);
          setIsAdmin(data.isAdmin || false);
          setIsUserBlocked(data.isBlocked || false);
          setBlockInfo(data.blockInfo || null);
        } else {
          // Usuario nuevo: no usar displayName de Auth si es igual al email
          const authDisplayName = user.displayName && user.email &&
            user.displayName.trim().toLowerCase() === user.email.trim().toLowerCase()
            ? null
            : (user.displayName || null);
          setUserProfileData({
            email: user.email,
            displayName: authDisplayName,
            photoURL: user.photoURL,
            createdAt: new Date()
          });
          setIsAdmin(false);
          setIsUserBlocked(false);
        }
      } catch (error) {
        console.error('Error cargando perfil de usuario:', error);
        setUserProfileData(null);
      } finally {
        setIsInitialized(true);
      }
    };

    loadUserProfile();
  }, [user]);

  // Exponer estado global mínimo para componentes que no usan el contexto
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.__ratafitAuth = {
        isPremium: !!userProfileData?.isPremium,
        subscription: userProfileData?.subscription || null,
        uid: user?.uid || null
      };
    }
  }, [user, userProfileData]);

  const value = {
    user,
    loading,
    error,
    userProfileData,
    isPremium: !!userProfileData?.isPremium,
    subscription: userProfileData?.subscription || null,
    setUserProfileData,
    isAdmin,
    isUserBlocked,
    blockInfo,
    isInitialized,
    loginInProgress,
    loginError,
    signInWithGoogle,
    signInWithGoogleForWelcome,
    signInWithGooglePopup,
    signInWithGoogleRedirect
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 